import { deleteMyTags, postMyTags } from '#api/accounts';
import { Button } from '#atoms';
import usePatchCurrentStepMutation from '#hooks/queries/onboard/usePatchCurrentStepMutation';
import { FloatButtonContainer } from '#molecules';
import { useOnBoardStep } from '#pages/OnBoardStep/OnBoardStep.hooks';
import { useToast } from '#providers/ToastProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import styled from 'styled-components';
import TagList from './TagList';

interface Step4Props {}

const Step4 = ({}: Step4Props) => {
  const { showToast } = useToast();

  const { setStep } = useOnBoardStep();
  const { mutateAsync: patchCurrentStep } = usePatchCurrentStepMutation();

  const queryClient = useQueryClient();

  const previousTags: string[] =
    queryClient
      .getMutationCache()
      .findAll({ mutationKey: ['/accounts/self/tags'] })
      .pop()?.state.variables ?? [];

  const { mutate: resetTags } = useMutation(
    ['/accounts/self/tags/reset'],
    deleteMyTags,
    {
      onSuccess: () => {
        mutate(selectedTags);
      },
      onError: () => {
        showToast({
          type: 'fail',
          message: '전문분야 설정에 실패하였습니다.'
        });
      }
    }
  );

  const { mutate } = useMutation(['/accounts/self/tags'], postMyTags, {
    onSuccess: () => {
      setStep(5);
      patchCurrentStep(5);
    },
    onError: () => {
      showToast({
        type: 'fail',
        message: '전문분야 설정에 실패하였습니다.'
      });
    }
  });

  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const onClick = (tag: string) => {
    const isSelected = selectedTags.includes(tag);

    if (selectedTags.length === 3 && !isSelected) {
      showToast({
        type: 'fail',
        message: '최대 3개까지 선택이 가능합니다.'
      });
    } else {
      if (isSelected) {
        setSelectedTags(prev => prev.filter(item => item !== tag));
      } else {
        setSelectedTags(prev => [...prev, tag]);
      }
    }
  };

  const onNext = () => {
    resetTags(previousTags);
  };

  return (
    <div className="h-[calc(100dvh-139px)] flex flex-col justify-between">
      <div className="flex flex-col gap-40">
        <div className="flex flex-col gap-24">
          <Title>나의 전문 분야를{'\n'}선택해 주세요</Title>
          <Description>최대 3개까지 선택 가능합니다.</Description>
        </div>

        <TagList selectedTags={selectedTags} onClick={onClick} />
      </div>

      <FloatButtonContainer>
        <Button
          disabled={!selectedTags.length || selectedTags.length > 3}
          onClick={onNext}
        >
          다음으로
        </Button>
      </FloatButtonContainer>
    </div>
  );
};

export const Title = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.gray15};
  white-space: pre-wrap;
`;

const Description = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.gray100};
  white-space: pre-wrap;
`;

export default Step4;
